import {createSlice} from '@reduxjs/toolkit'
import PaymentService from '../../api/payment'
import {toast} from 'react-toastify'
import {getUpgradedPlan} from '../sidebar'
import FormHelper from '../../utils/forms'
import {doNothing} from '../../utils/placeholder'

export const paymentSlice = createSlice({
    name: 'payments',
    initialState: {
        coupon: null,
        subscriptions: {},
        paymentData: {},
        prorationData: {},
        isFetching: false,
        isDeleting: false,
        isPaymentFetching: true,
        paymentMethods: {}
    },
    reducers: {
        setSubscriptions: (state, {payload}) => {
            state.subscriptions = payload
        },
        setPaymentData: (state, {payload}) => {
            state.paymentData = payload
        },
        setProrationData: (state, {payload}) => {
            state.prorationData = payload
        },
        setIsFetching: (state, {payload}) => {
            state.isFetching = payload
        },
        setIsDeleting: (state, {payload}) => {
            state.isDeleting = payload
        },
        setIsPaymentFetching: (state, {payload}) => {
            state.isPaymentFetching = payload
        },
        setPaymentMethods: (state, {payload}) => {
            state.paymentMethods = payload
        },
        setCoupon: (state, {payload}) => {
            state.coupon = payload
        }
    }
})

export const {
    setSubscriptions,
    setPaymentData,
    setProrationData,
    setIsFetching,
    setIsDeleting,
    setIsPaymentFetching,
    setPaymentMethods,
    setCoupon
} = paymentSlice.actions

export default paymentSlice.reducer

export const getCoupon = (couponCode, setFieldError) => {
    return async dispatch => {
        try {
            const searchParams = new URLSearchParams(window.location.search)
            if (!searchParams.get('coupon') && !setFieldError) {
                return
            }
            const {data} = await PaymentService.getCoupon(couponCode)
            const coupon = data?.data?.coupon
            localStorage.setItem('coupon', couponCode)
            dispatch(setCoupon(coupon))
            return true
        } catch (e) {
            dispatch(setCoupon(null))
            localStorage.removeItem('coupon')
            const res = e?.response?.data
            const fields = res?.data?.fields
            fields.code = fields.common
            delete fields.common
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
    }
}

export const getSubscriptions = (showLoader = false, applyCouponCode = false) => {
    return async dispatch => {
        showLoader && dispatch(setIsFetching(true))
        try {
            const {data} = await PaymentService.getSubscriptions()
            const couponCode = localStorage.getItem('coupon')
            if (couponCode) {
                dispatch(getCoupon(couponCode, applyCouponCode))
            }
            dispatch(setSubscriptions(data))
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
        showLoader && dispatch(setIsFetching(false))
    }
}

export const getSubscriptionPayments = id => {
    return async dispatch => {
        dispatch(setIsPaymentFetching(true))
        try {
            const {data} = await PaymentService.getSubscriptionPayments(id)
            dispatch(setPaymentData(data))
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
        dispatch(setIsPaymentFetching(false))
    }
}

export const deleteSubscription = id => {
    return async dispatch => {
        dispatch(setIsDeleting(true))
        try {
            const {data} = await PaymentService.deleteSubscription(id)
            toast.success(data?.message)
            dispatch(setIsDeleting(false))
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
        dispatch(setIsDeleting(false))
    }
}

export const reactivateSubscription = id => {
    return async() => {
        try {
            const {data} = await PaymentService.reactivateSubscription(id)
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}

export const updateSubscription = (setShowLoader, id, data) => {
    return async dispatch => {
        setShowLoader(true)
        try {
            const response = await PaymentService.updateSubscription(id, data)
            dispatch(getUpgradedPlan())
            toast.success(response?.data.message)
            setShowLoader(false)
            return true
        } catch (e) {
            setShowLoader(false)
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}

export const createSession = (setShowLoader, data) => {
    return async() => {
        setShowLoader(true)
        try {
            const session = await PaymentService.createSession(data)
            toast.success(session?.data.message)
            window.location.replace(session?.data.data.url)
            setShowLoader(false)
        } catch (e) {
            setShowLoader(false)
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}

export const createSubscriptionSession = (setShowLoader, id) => {
    return async() => {
        try {
            const session = await PaymentService.createSubscriptionSession(id)
            toast.success(session?.data.message)
            window.location.replace(session?.data.data.url)
            setShowLoader(false)
        } catch (e) {
            setShowLoader(false)
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}

export const getPaymentMethods = () => {
    return async dispatch => {
        dispatch(setIsFetching(true))
        try {
            const {data} = await PaymentService.getPaymentMethods()
            dispatch(setPaymentMethods(data))
        } catch (e) {
            doNothing()
        }
        dispatch(setIsFetching(false))
    }
}

export const getProration = (setShowLoader, id, productId) => {
    return async dispatch => {
        setShowLoader(true)
        try {
            const {data} = await PaymentService.getProration(id, productId)
            setShowLoader(false)
            await dispatch(setProrationData(data?.data))
            return true
        } catch (e) {
            doNothing()
        }
        setShowLoader(false)
    }
}
